// @ts-nocheck
/**
 * TNF Main Collection
 * Handles:
 * - Filter pill dropdowns (open/close, checkbox filters, price range)
 * - Sort dropdown
 * - URL-based filter application (navigates with query params)
 * - Per-card image slider (prev/next, dots)
 * - Color swatch selection (active state)
 * - Size picker + add to cart
 * - Progressive autoload when the user reaches the end of the product grid
 */
(function () {
  'use strict';

  var sizePickerOpenEventName = 'tnf:size-picker:open';
  var storefrontDiscountUpdateEventName = 'storefront-discount-prices:updated';
  var swatchOverflowEventsBound = false;
  var swatchOverflowResizeTimeout;

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    var section = document.querySelector('.tnf-mc');
    if (!section) return;

    bindSwatchOverflowGlobalEvents();

    initFilterPills(section);
    initSortDropdown(section);
    initFilterCheckboxes(section);
    initPriceFilters(section);
    initCloseOnOutsideClick(section);
    initCustomFilters(section); // Custom metafield filters
    initMobileFilterDrawer(section);
    initClientPriceSorting(section);

    var cards = section.querySelectorAll('[data-product-card]');
    registerCardSortIndices(section, cards);
    initProductCards(cards);
    initInfiniteScroll(section);
    initGoTopButton(section);
  }

  function getCurrentSortValue(section) {
    var url = new URL(window.location.href);
    var urlSortValue = url.searchParams.get('sort_by');
    if (urlSortValue) return urlSortValue;

    var activeOption = section && section.querySelector('.tnf-mc__sort-option.is-active');
    return activeOption ? activeOption.getAttribute('data-sort-value') || 'manual' : 'manual';
  }

  function isClientPriceSort(sortValue) {
    return sortValue === 'price-ascending' || sortValue === 'price-descending';
  }

  function registerCardSortIndices(section, cards) {
    if (typeof section.__tnfSortIndexCounter !== 'number') {
      section.__tnfSortIndexCounter = 0;
    }

    Array.from(cards || []).forEach(function (card) {
      if (!(card instanceof HTMLElement) || card.hasAttribute('data-sort-index')) return;
      card.setAttribute('data-sort-index', String(section.__tnfSortIndexCounter));
      section.__tnfSortIndexCounter += 1;
    });
  }

  function getCardEffectivePrice(card) {
    if (!(card instanceof HTMLElement)) return Number.POSITIVE_INFINITY;

    var priceContainer = card.querySelector('[data-discount-price]');
    if (!(priceContainer instanceof HTMLElement)) return Number.POSITIVE_INFINITY;

    var currentPriceCents = parseInt(priceContainer.getAttribute('data-current-price-cents') || '0', 10);
    if (Number.isFinite(currentPriceCents) && currentPriceCents > 0) {
      return currentPriceCents;
    }

    return Number.POSITIVE_INFINITY;
  }

  function applyClientPriceSort(section) {
    var sortValue = getCurrentSortValue(section);
    if (!isClientPriceSort(sortValue)) return;

    var grid = section.querySelector('[data-product-grid]');
    if (!(grid instanceof HTMLElement)) return;

    var cards = Array.from(grid.querySelectorAll('[data-product-card]'));
    if (cards.length < 2) return;

    cards.sort(function (leftCard, rightCard) {
      var leftPrice = getCardEffectivePrice(leftCard);
      var rightPrice = getCardEffectivePrice(rightCard);

      if (leftPrice !== rightPrice) {
        return sortValue === 'price-descending' ? rightPrice - leftPrice : leftPrice - rightPrice;
      }

      var leftIndex = parseInt(leftCard.getAttribute('data-sort-index') || '0', 10);
      var rightIndex = parseInt(rightCard.getAttribute('data-sort-index') || '0', 10);
      return leftIndex - rightIndex;
    });

    var fragment = document.createDocumentFragment();
    cards.forEach(function (card) {
      fragment.appendChild(card);
    });
    grid.appendChild(fragment);
  }

  function scheduleClientPriceSort(section) {
    if (!isClientPriceSort(getCurrentSortValue(section))) return;

    if (hasActiveQuickBuyInteraction(section)) {
      section.__tnfPendingClientPriceSort = true;
      return;
    }

    if (typeof section.__tnfPriceSortTimeout === 'number') {
      clearTimeout(section.__tnfPriceSortTimeout);
    }

    // Price hydration dispatches one event per card. Coalesce those bursts so the
    // grid is not re-ordered continuously while the shopper is trying to click.
    section.__tnfPriceSortTimeout = setTimeout(function () {
      section.__tnfPriceSortTimeout = null;
      requestAnimationFrame(function () {
        applyClientPriceSort(section);
      });
    }, 180);
  }

  function scheduleClientFilters(section) {
    if (!hasActiveClientFilters(section)) return;

    if (hasActiveQuickBuyInteraction(section)) {
      section.__tnfPendingClientFilters = true;
      return;
    }

    if (typeof section.__tnfClientFilterFrame === 'number') {
      cancelAnimationFrame(section.__tnfClientFilterFrame);
    }

    section.__tnfClientFilterFrame = requestAnimationFrame(function () {
      section.__tnfClientFilterFrame = null;
      applyCustomFilters(section);
    });
  }

  function initClientPriceSorting(section) {
    document.addEventListener(storefrontDiscountUpdateEventName, function (event) {
      if (!(event.target instanceof HTMLElement) || !section.contains(event.target)) return;
      scheduleClientFilters(section);
      scheduleClientPriceSort(section);
    });

    scheduleClientFilters(section);
    scheduleClientPriceSort(section);
  }

  function hasActiveQuickBuyInteraction(section) {
    if (!(section instanceof HTMLElement)) return false;

    var popupOverlay = section.querySelector('[data-popup-overlay]');
    if (popupOverlay instanceof HTMLElement && popupOverlay.getAttribute('aria-hidden') === 'false') {
      return true;
    }

    if (section.querySelector('[data-size-picker].is-open')) {
      return true;
    }

    return Boolean(getActiveInteractionCard(section));
  }

  function getActiveInteractionCard(section) {
    if (!(section instanceof HTMLElement)) return null;

    var activeCard = section.__tnfActiveInteractionCard;
    if (activeCard instanceof HTMLElement && section.contains(activeCard)) {
      return activeCard;
    }

    section.__tnfActiveInteractionCard = null;
    return null;
  }

  function setActiveInteractionCard(section, card) {
    if (!(section instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
    section.__tnfActiveInteractionCard = card;
  }

  function clearActiveInteractionCard(section, card) {
    var activeCard = getActiveInteractionCard(section);
    if (!activeCard) return;
    if (card instanceof HTMLElement && activeCard !== card) return;

    section.__tnfActiveInteractionCard = null;
    scheduleDeferredClientSyncFlush(section);
  }

  function flushDeferredClientSync(section) {
    if (!(section instanceof HTMLElement) || hasActiveQuickBuyInteraction(section)) return;

    var shouldApplyFilters = section.__tnfPendingClientFilters === true;
    var shouldApplyPriceSort = section.__tnfPendingClientPriceSort === true;

    section.__tnfPendingClientFilters = false;
    section.__tnfPendingClientPriceSort = false;

    if (shouldApplyFilters) {
      scheduleClientFilters(section);
    }

    if (shouldApplyPriceSort) {
      scheduleClientPriceSort(section);
    }
  }

  function scheduleDeferredClientSyncFlush(section) {
    if (!(section instanceof HTMLElement)) return;

    if (typeof section.__tnfDeferredClientSyncFlushFrame === 'number') {
      cancelAnimationFrame(section.__tnfDeferredClientSyncFlushFrame);
    }

    section.__tnfDeferredClientSyncFlushFrame = requestAnimationFrame(function () {
      section.__tnfDeferredClientSyncFlushFrame = null;
      flushDeferredClientSync(section);
    });
  }

  function bindSwatchOverflowGlobalEvents() {
    if (swatchOverflowEventsBound) return;
    swatchOverflowEventsBound = true;

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) {
        closeAllSwatchOverflowPanels();
        return;
      }

      if (target.closest('[data-swatch-overflow-toggle]') || target.closest('[data-swatch-overflow-panel]')) {
        return;
      }

      closeAllSwatchOverflowPanels();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeAllSwatchOverflowPanels();
      }
    });

    window.addEventListener('resize', function () {
      clearTimeout(swatchOverflowResizeTimeout);
      swatchOverflowResizeTimeout = setTimeout(function () {
        document.querySelectorAll('.tnf-pccarousel__swatches').forEach(syncSwatchOverflowContainer);
      }, 120);
    }, { passive: true });
  }

  function initSwatchOverflow(card) {
    syncSwatchOverflowContainers(card);
  }

  function syncSwatchOverflowContainers(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('.tnf-pccarousel__swatches').forEach(syncSwatchOverflowContainer);
  }

  function syncSwatchOverflowContainer(swatchesContainer) {
    if (!(swatchesContainer instanceof HTMLElement)) return;

    var shouldRestoreOpenState = isSwatchOverflowPanelOpen(swatchesContainer);

    resetSwatchOverflowContainer(swatchesContainer);

    if (shouldSkipSwatchOverflow(swatchesContainer)) return;

    var swatches = Array.from(swatchesContainer.querySelectorAll('[data-group-swatch], [data-swatch-btn]'));
    var visibleLimit = getVisibleSwatchLimit();
    if (swatches.length <= visibleLimit) return;

    var visibleSwatches = getVisibleSwatches(swatches, visibleLimit);
    var overflowSwatches = swatches.filter(function (swatch) {
      return visibleSwatches.indexOf(swatch) === -1;
    });

    if (!overflowSwatches.length) return;

    var visibleFragment = document.createDocumentFragment();
    visibleSwatches.forEach(function (swatch) {
      visibleFragment.appendChild(swatch);
    });
    swatchesContainer.appendChild(visibleFragment);

    var toggle = createSwatchOverflowToggle(overflowSwatches.length);
    swatchesContainer.appendChild(toggle);

    var overflowPanel = document.createElement('div');
    overflowPanel.className = 'tnf-pccarousel__swatches-overflow';
    overflowPanel.hidden = true;
    overflowPanel.setAttribute('data-swatch-overflow-panel', '');

    var overflowFragment = document.createDocumentFragment();
    overflowSwatches.forEach(function (swatch) {
      overflowFragment.appendChild(swatch);
    });
    overflowPanel.appendChild(overflowFragment);
    swatchesContainer.insertAdjacentElement('afterend', overflowPanel);
    swatchesContainer.classList.add('has-overflow');

    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      var shouldOpen = overflowPanel.hidden;

      if (!shouldOpen) {
        closeSwatchOverflowPanel(swatchesContainer);
        return;
      }

      closeAllSwatchOverflowPanels();
      openSwatchOverflowPanel(swatchesContainer);
    });

    if (shouldRestoreOpenState) {
      openSwatchOverflowPanel(swatchesContainer);
    }
  }

  function resetSwatchOverflowContainer(swatchesContainer) {
    if (!(swatchesContainer instanceof HTMLElement)) return;

    var overflowPanel = getSwatchOverflowPanel(swatchesContainer);
    if (overflowPanel) {
      Array.from(overflowPanel.querySelectorAll('[data-group-swatch], [data-swatch-btn]')).forEach(function (swatch) {
        swatchesContainer.appendChild(swatch);
      });
      overflowPanel.remove();
    }

    var toggle = swatchesContainer.querySelector('[data-swatch-overflow-toggle]');
    if (toggle) {
      toggle.remove();
    }

    swatchesContainer.classList.remove('has-overflow', 'is-overflow-open');
  }

  function getSwatchOverflowPanel(swatchesContainer) {
    var nextElement = swatchesContainer.nextElementSibling;
    if (nextElement && nextElement.hasAttribute('data-swatch-overflow-panel')) {
      return nextElement;
    }

    return null;
  }

  function closeAllSwatchOverflowPanels(exceptionContainer) {
    document.querySelectorAll('.tnf-pccarousel__swatches.has-overflow').forEach(function (swatchesContainer) {
      if (exceptionContainer && swatchesContainer === exceptionContainer) return;
      closeSwatchOverflowPanel(swatchesContainer);
    });
  }

  function closeSwatchOverflowPanel(swatchesContainer) {
    if (!(swatchesContainer instanceof HTMLElement)) return;

    var overflowPanel = getSwatchOverflowPanel(swatchesContainer);
    if (overflowPanel) {
      overflowPanel.hidden = true;
    }

    var toggle = swatchesContainer.querySelector('[data-swatch-overflow-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }

    swatchesContainer.classList.remove('is-overflow-open');
  }

  function openSwatchOverflowPanel(swatchesContainer) {
    if (!(swatchesContainer instanceof HTMLElement)) return;

    var overflowPanel = getSwatchOverflowPanel(swatchesContainer);
    var toggle = swatchesContainer.querySelector('[data-swatch-overflow-toggle]');
    if (!overflowPanel || !toggle) return;

    swatchesContainer.classList.add('is-overflow-open');
    overflowPanel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
  }

  function isSwatchOverflowPanelOpen(swatchesContainer) {
    return swatchesContainer instanceof HTMLElement && swatchesContainer.classList.contains('is-overflow-open');
  }

  function createSwatchOverflowToggle(hiddenCount) {
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'tnf-pccarousel__swatch tnf-pccarousel__swatch--more';
    toggle.setAttribute('data-swatch-overflow-toggle', '');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Ver ' + hiddenCount + ' colores más');
    toggle.innerHTML = '<span class="tnf-pccarousel__swatch-more-symbol" aria-hidden="true">+</span>';
    return toggle;
  }

  function getVisibleSwatches(swatches, visibleLimit) {
    var activeIndex = swatches.findIndex(function (swatch) {
      return swatch.classList.contains('is-active') || swatch.getAttribute('aria-current') === 'true';
    });

    if (activeIndex === -1 || activeIndex < visibleLimit) {
      return swatches.slice(0, visibleLimit);
    }

    var visibleSwatches = swatches.slice(0, Math.max(visibleLimit - 1, 0));
    visibleSwatches.push(swatches[activeIndex]);
    return visibleSwatches;
  }

  function getVisibleSwatchLimit() {
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (viewportWidth <= 749) return 4;
    if (viewportWidth <= 989) return 6;
    return 8;
  }

  function shouldSkipSwatchOverflow(swatchesContainer) {
    return isMobileViewport() && Boolean(swatchesContainer.closest('.tnf-pccarousel__popup-quickbuy'));
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function hasSelectedNativeFilters(section) {
    return Boolean(section.querySelector('[data-filter-checkbox]:checked'));
  }

  function hasFilledPriceFilters(section) {
    return Array.from(section.querySelectorAll('.tnf-mc__price-input')).some(function (input) {
      return Boolean(input.value);
    });
  }

  function parseCurrencyUnits(value) {
    if (value == null) return null;

    var digits = String(value).replace(/[^\d]/g, '');
    if (!digits) return null;

    var parsedValue = parseInt(digits, 10);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  function getActivePriceRangeCents() {
    var url = new URL(window.location.href);
    var minUnits = parseCurrencyUnits(url.searchParams.get('filter.v.price.gte'));
    var maxUnits = parseCurrencyUnits(url.searchParams.get('filter.v.price.lte'));

    return {
      min: minUnits == null ? null : minUnits * 100,
      max: maxUnits == null ? null : maxUnits * 100,
    };
  }

  function hasActivePriceRange() {
    var activePriceRange = getActivePriceRangeCents();
    return activePriceRange.min !== null || activePriceRange.max !== null;
  }

  function hasActiveClientFilters(section) {
    return Boolean(section.querySelector('[data-custom-checkbox]:checked')) || hasActivePriceRange();
  }

  function cardMatchesActivePriceRange(card, activePriceRange) {
    if (!activePriceRange || (activePriceRange.min === null && activePriceRange.max === null)) {
      return true;
    }

    var priceContainer = card.querySelector('[data-discount-price]');
    if (!(priceContainer instanceof HTMLElement)) return true;

    var currentPriceCents = parseInt(priceContainer.getAttribute('data-current-price-cents') || '0', 10);
    if (!Number.isFinite(currentPriceCents) || currentPriceCents <= 0) return true;

    if (activePriceRange.min !== null && currentPriceCents < activePriceRange.min) return false;
    if (activePriceRange.max !== null && currentPriceCents > activePriceRange.max) return false;

    return true;
  }

  function buildFilterUrl(section) {
    var url = new URL(window.location.href);
    var keysToRemove = [];

    url.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) {
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach(function (key) {
      url.searchParams.delete(key);
    });

    url.searchParams.delete('mejor_uso');
    url.searchParams.delete('tecnologias');
    url.searchParams.delete('genero');
    url.searchParams.delete('talla');
    url.searchParams.delete('page');

    section.querySelectorAll('[data-filter-checkbox]:checked').forEach(function (checkbox) {
      var paramName = checkbox.getAttribute('data-param-name');
      var paramValue = checkbox.getAttribute('data-param-value');

      if (paramName && paramValue) {
        url.searchParams.append(paramName, paramValue);
      }
    });

    section.querySelectorAll('[data-price-filter]').forEach(function (priceFilter) {
      var minInput = priceFilter.querySelector('[data-price-min]');
      var maxInput = priceFilter.querySelector('[data-price-max]');

      if (minInput && minInput.value) {
        url.searchParams.set(minInput.name, minInput.value);
      }

      if (maxInput && maxInput.value) {
        url.searchParams.set(maxInput.name, maxInput.value);
      }
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="mejor_uso"]:checked').forEach(function (checkbox) {
      url.searchParams.append('mejor_uso', checkbox.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="tecnologias"]:checked').forEach(function (checkbox) {
      url.searchParams.append('tecnologias', checkbox.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="genero"]:checked').forEach(function (checkbox) {
      url.searchParams.append('genero', checkbox.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="talla"]:checked').forEach(function (checkbox) {
      url.searchParams.append('talla', checkbox.getAttribute('data-filter-value'));
    });

    return url;
  }

  function refreshCardDiscount(card, variantId) {
    window.StorefrontDiscountPrices?.refreshCard(card, variantId);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return character;
      }
    });
  }

  function syncSelectItemCardTracking(card, source) {
    if (!(card instanceof HTMLElement) || !(source instanceof HTMLElement)) return;

    var analytics = window.TNFSelectItemAnalytics;
    if (analytics && typeof analytics.syncSelectItemTracking === 'function') {
      analytics.syncSelectItemTracking(card, source);
    }

    var nextPriceCents = source.getAttribute('data-current-price-cents');
    var priceContainer = card.querySelector('[data-discount-price]');
    if (nextPriceCents && priceContainer instanceof HTMLElement) {
      priceContainer.setAttribute('data-current-price-cents', nextPriceCents);
    }
  }

  function parseGroupedSizePayload(swatch) {
    var rawOptions = swatch.getAttribute('data-product-size-options');
    var options = [];

    if (rawOptions) {
      try {
        var parsed = JSON.parse(rawOptions);
        if (Array.isArray(parsed)) {
          options = parsed;
        }
      } catch (error) {
        options = [];
      }
    }

    return {
      label: swatch.getAttribute('data-product-size-label') || 'Talla',
      options: options
    };
  }

  function parseGroupedImagePayload(swatch) {
    var rawImages = swatch.getAttribute('data-product-images');
    var title = swatch.getAttribute('data-product-title') || '';
    var images = [];

    if (rawImages) {
      try {
        var parsed = JSON.parse(rawImages);
        if (Array.isArray(parsed)) {
          images = parsed.filter(function (image) {
            return image && image.src;
          });
        }
      } catch (error) {
        images = [];
      }
    }

    if (!images.length) {
      var fallbackImage = swatch.getAttribute('data-product-image');
      if (fallbackImage) {
        images = [{
          src: fallbackImage,
          srcset: '',
          alt: title
        }];
      }
    }

    return images;
  }

  function buildCardSlidesMarkup(images, productUrl, productTitle) {
    var safeUrl = productUrl || '#';
    var safeTitle = productTitle || '';

    return images.map(function (image, index) {
      var src = image && image.src ? image.src : '';
      var srcset = image && image.srcset ? ' srcset="' + escapeHtml(image.srcset) + '"' : '';
      var width = image && image.width ? ' width="' + escapeHtml(image.width) + '"' : '';
      var height = image && image.height ? ' height="' + escapeHtml(image.height) + '"' : '';
      var alt = image && image.alt ? image.alt : safeTitle;

      return '<div class="tnf-pccarousel__image-slide' +
        (index === 0 ? ' is-active' : '') +
        '" data-slide-index="' + index + '">' +
        '<a href="' + escapeHtml(safeUrl) + '" aria-label="' + escapeHtml(safeTitle) + '">' +
        '<img class="tnf-pccarousel__image" loading="lazy" src="' + escapeHtml(src) + '"' +
        srcset +
        ' sizes="(min-width: 990px) 324px, (min-width: 750px) 33vw, 50vw" alt="' + escapeHtml(alt) + '"' +
        width +
        height +
        '>' +
        '</a>' +
        '</div>';
    }).join('');
  }

  function buildCardDotsMarkup(imageCount) {
    var dots = [];

    for (var index = 0; index < imageCount; index += 1) {
      dots.push('<button class="tnf-pccarousel__img-dot' +
        (index === 0 ? ' is-active' : '') +
        '" data-card-dot="' + index + '" aria-label="Imagen ' + (index + 1) + '"></button>');
    }

    return dots.join('');
  }

  function syncCardSliderMedia(card, productUrl, productTitle, images) {
    var slider = card.querySelector('[data-card-slider]');
    if (!slider || !images.length) return;

    var imagesTrack = slider.querySelector('.tnf-pccarousel__images-track');
    if (!imagesTrack) return;

    imagesTrack.innerHTML = buildCardSlidesMarkup(images, productUrl, productTitle);

    var dotsContainer = slider.querySelector('.tnf-pccarousel__img-dots');
    var prevBtn = slider.querySelector('[data-card-prev]');
    var nextBtn = slider.querySelector('[data-card-next]');
    var hasMultipleImages = images.length > 1;

    if (dotsContainer) {
      dotsContainer.innerHTML = hasMultipleImages ? buildCardDotsMarkup(images.length) : '';
      dotsContainer.hidden = !hasMultipleImages;
    }

    if (prevBtn) {
      prevBtn.hidden = !hasMultipleImages;
    }

    if (nextBtn) {
      nextBtn.hidden = !hasMultipleImages;
    }

    if (card.__tnfCardSlider && typeof card.__tnfCardSlider.reset === 'function') {
      card.__tnfCardSlider.reset();
    }
  }

  function initProductCards(cards) {
    cards.forEach(refreshCardDiscount);
    cards.forEach(initCardSlider);
    cards.forEach(initSwatches);
    cards.forEach(initGroupSwatches);
    cards.forEach(initSwatchOverflow);
    cards.forEach(initSizePicker);
  }

  function initInfiniteScroll(section) {
    var grid = section.querySelector('[data-product-grid]');
    var autoload = section.querySelector('[data-infinite-scroll]');
    if (!grid || !autoload || typeof IntersectionObserver === 'undefined') return;

    var status = autoload.querySelector('[data-infinite-scroll-status]');
    var sectionId = section.getAttribute('data-section-id');
    var isLoading = false;

    function setAutoloadState(state) {
      autoload.dataset.state = state;
      autoload.setAttribute('aria-busy', String(state === 'loading'));

      if (!status) return;

      if (state === 'loading') {
        status.hidden = false;
        status.textContent = autoload.getAttribute('data-loading-text') || 'Cargando más productos...';
      } else if (state === 'error') {
        status.hidden = false;
        status.textContent = autoload.getAttribute('data-error-text') || 'No se pudieron cargar más productos.';
      } else {
        status.hidden = true;
      }
    }

    function shouldApplyCustomFilters() {
      return hasActiveClientFilters(section);
    }

    function cleanupAutoload() {
      observer.disconnect();
      autoload.remove();
    }

    function maybeLoadAgain() {
      if (isLoading) return;

      var rect = autoload.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 500) {
        loadNextPage();
      }
    }

    function buildRequestUrl(nextUrl) {
      var requestUrl = new URL(nextUrl, window.location.origin);
      if (sectionId) {
        requestUrl.searchParams.set('section_id', sectionId);
      }
      return requestUrl;
    }

    function loadNextPage() {
      var nextUrl = autoload.getAttribute('data-next-url');
      if (!nextUrl || isLoading) return;

      isLoading = true;
      setAutoloadState('loading');

      fetch(buildRequestUrl(nextUrl).toString(), {
        headers: {
          Accept: 'text/html',
        },
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Failed to fetch next collection page');
          }

          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = parsed.querySelector('[data-product-grid]');
          if (!nextGrid) {
            throw new Error('Product grid not found in autoload response');
          }

          var newCards = Array.from(nextGrid.querySelectorAll('[data-product-card]'));
          if (!newCards.length) {
            cleanupAutoload();
            return;
          }

          var fragment = document.createDocumentFragment();
          newCards.forEach(function (card) {
            fragment.appendChild(card);
          });
          grid.appendChild(fragment);

          registerCardSortIndices(section, newCards);
          initProductCards(newCards);

          if (shouldApplyCustomFilters()) {
            scheduleClientFilters(section);
          }

          scheduleClientPriceSort(section);

          var nextAutoload = parsed.querySelector('[data-infinite-scroll]');
          var followingUrl = nextAutoload?.getAttribute('data-next-url');

          if (followingUrl) {
            autoload.setAttribute('data-next-url', followingUrl);
            setAutoloadState('idle');
            requestAnimationFrame(maybeLoadAgain);
          } else {
            cleanupAutoload();
          }
        })
        .catch(function (error) {
          console.error('[tnf-main-collection] autoload failed', error);
          setAutoloadState('error');
        })
        .finally(function () {
          isLoading = false;
        });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadNextPage();
          }
        });
      },
      {
        rootMargin: '500px 0px',
      }
    );

    setAutoloadState('idle');
    observer.observe(autoload);
  }

  function initGoTopButton(section) {
    var goTopButton = section.querySelector('[data-go-top]');
    if (!(goTopButton instanceof HTMLButtonElement)) return;

    var ticking = false;

    function getHeaderOffset() {
      var headerGroup = document.querySelector('#header-group');
      return headerGroup instanceof HTMLElement ? headerGroup.getBoundingClientRect().height : 0;
    }

    function getSectionTopOffset() {
      var sectionTop = window.scrollY + section.getBoundingClientRect().top;
      return Math.max(sectionTop - getHeaderOffset() - 16, 0);
    }

    function updateVisibility() {
      var revealThreshold = getSectionTopOffset() + Math.max(window.innerHeight * 0.75, 320);
      goTopButton.classList.toggle('is-visible', window.scrollY > revealThreshold);
    }

    function requestVisibilityUpdate() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        updateVisibility();
      });
    }

    goTopButton.addEventListener('click', function () {
      window.scrollTo({
        top: getSectionTopOffset(),
        behavior: 'smooth',
      });
    });

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
    window.addEventListener('resize', requestVisibilityUpdate, { passive: true });

    updateVisibility();
  }

  /* ==========================================================================
     Custom Metafield Filters (Client-side filtering)
     ========================================================================== */

  function initCustomFilters(section) {
    var customCheckboxes = section.querySelectorAll('[data-custom-checkbox]');
    if (!customCheckboxes.length) return;

    // Restore state from URL params
    restoreCustomFilterState(section);

    customCheckboxes.forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        if (isMobileViewport()) {
          updateCustomFilterCounts(section);
          return;
        }

        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
    });

    // Initial count update
    updateCustomFilterCounts(section);
  }

  function restoreCustomFilterState(section) {
    var url = new URL(window.location.href);
    
    // Restore mejor_uso filters
    var mejorUsoParams = url.searchParams.getAll('mejor_uso');
    mejorUsoParams.forEach(function (value) {
      var checkbox = section.querySelector('[data-custom-checkbox][data-filter-type="mejor_uso"][data-filter-value="' + value + '"]');
      if (checkbox) checkbox.checked = true;
    });

    // Restore tecnologias filters
    var tecnologiasParams = url.searchParams.getAll('tecnologias');
    tecnologiasParams.forEach(function (value) {
      var checkbox = section.querySelector('[data-custom-checkbox][data-filter-type="tecnologias"][data-filter-value="' + value + '"]');
      if (checkbox) checkbox.checked = true;
    });

    // Restore genero filters
    var generoParams = url.searchParams.getAll('genero');
    generoParams.forEach(function (value) {
      var checkbox = section.querySelector('[data-custom-checkbox][data-filter-type="genero"][data-filter-value="' + value + '"]');
      if (checkbox) checkbox.checked = true;
    });

    // Restore talla filters
    var tallaParams = url.searchParams.getAll('talla');
    tallaParams.forEach(function (value) {
      var checkbox = section.querySelector('[data-custom-checkbox][data-filter-type="talla"][data-filter-value="' + value + '"]');
      if (checkbox) checkbox.checked = true;
    });

    // Apply filters if any were restored
    if (mejorUsoParams.length > 0 || tecnologiasParams.length > 0 || generoParams.length > 0 || tallaParams.length > 0) {
      applyCustomFilters(section);
      updateActiveFilterTags(section);
    }
  }

  function applyCustomFilters(section) {
    var cards = section.querySelectorAll('[data-product-card]');
    var grid = section.querySelector('[data-product-grid]');
    var activePriceRange = getActivePriceRangeCents();
    
    // Get selected filter values
    var selectedMejorUso = [];
    var selectedTecnologias = [];
    var selectedGenero = [];
    var selectedTalla = [];

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="mejor_uso"]:checked').forEach(function (cb) {
      selectedMejorUso.push(cb.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="tecnologias"]:checked').forEach(function (cb) {
      selectedTecnologias.push(cb.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="genero"]:checked').forEach(function (cb) {
      selectedGenero.push(cb.getAttribute('data-filter-value'));
    });

    section.querySelectorAll('[data-custom-checkbox][data-filter-type="talla"]:checked').forEach(function (cb) {
      selectedTalla.push(cb.getAttribute('data-filter-value'));
    });

    var visibleCount = 0;

    cards.forEach(function (card) {
      var cardMejorUso = (card.getAttribute('data-mejor-uso') || '').split(',').filter(Boolean);
      var cardTecnologias = (card.getAttribute('data-tecnologias') || '').split(',').filter(Boolean);
      var cardGenero = (card.getAttribute('data-genero') || '').split(',').filter(Boolean);
      var cardTallas = (card.getAttribute('data-tallas') || '').split(',').filter(Boolean);

      var matchesMejorUso = selectedMejorUso.length === 0 || selectedMejorUso.some(function (val) {
        return cardMejorUso.includes(val);
      });

      var matchesTecnologias = selectedTecnologias.length === 0 || selectedTecnologias.some(function (val) {
        return cardTecnologias.includes(val);
      });

      var matchesGenero = selectedGenero.length === 0 || selectedGenero.some(function (val) {
        return cardGenero.includes(val);
      });

      var matchesTalla = selectedTalla.length === 0 || selectedTalla.some(function (val) {
        return cardTallas.includes(val);
      });

      var matchesPriceRange = cardMatchesActivePriceRange(card, activePriceRange);

      if (matchesMejorUso && matchesTecnologias && matchesGenero && matchesTalla && matchesPriceRange) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    // Update product count display
    var countEl = section.querySelector('.tnf-mc__product-count');
    if (countEl) {
      countEl.textContent = visibleCount + ' productos';
    }

    // Show/hide empty state
    var emptyEl = section.querySelector('.tnf-mc__empty-wrapper');
    if (visibleCount === 0 && (selectedMejorUso.length > 0 || selectedTecnologias.length > 0 || selectedGenero.length > 0 || selectedTalla.length > 0 || activePriceRange.min !== null || activePriceRange.max !== null)) {
      if (!emptyEl) {
        emptyEl = document.createElement('div');
        emptyEl.className = 'tnf-mc__empty-wrapper';
        emptyEl.innerHTML = '<p class="tnf-mc__empty">No hay productos que coincidan con los filtros seleccionados.</p>';
        grid.parentNode.insertBefore(emptyEl, grid.nextSibling);
      }
      emptyEl.style.display = 'flex';
    } else if (emptyEl) {
      emptyEl.style.display = 'none';
    }
  }

  function updateCustomFilterCounts(section) {
    // Update pill badge counts
    var mejorUsoPill = section.querySelector('[data-custom-filter="mejor_uso"]');
    var tecnologiasPill = section.querySelector('[data-custom-filter="tecnologias"]');
    var generoPill = section.querySelector('[data-custom-filter="genero"]');
    var tallaPill = section.querySelector('[data-custom-filter="talla"]');

    if (mejorUsoPill) {
      var mejorUsoCount = section.querySelectorAll('[data-filter-type="mejor_uso"]:checked').length;
      var mejorUsoBadge = mejorUsoPill.querySelector('[data-filter-count]');
      if (mejorUsoBadge) {
        mejorUsoBadge.textContent = mejorUsoCount;
        mejorUsoBadge.style.display = mejorUsoCount > 0 ? 'inline-flex' : 'none';
      }
      // Update item active states
      section.querySelectorAll('[data-filter-type="mejor_uso"]').forEach(function (cb) {
        var item = cb.closest('.tnf-mc__uso-item');
        if (item) item.classList.toggle('is-active', cb.checked);
      });
    }

    if (tecnologiasPill) {
      var tecnologiasCount = section.querySelectorAll('[data-filter-type="tecnologias"]:checked').length;
      var tecnologiasBadge = tecnologiasPill.querySelector('[data-filter-count]');
      if (tecnologiasBadge) {
        tecnologiasBadge.textContent = tecnologiasCount;
        tecnologiasBadge.style.display = tecnologiasCount > 0 ? 'inline-flex' : 'none';
      }
    }

    if (generoPill) {
      var generoCount = section.querySelectorAll('[data-filter-type="genero"]:checked').length;
      var generoBadge = generoPill.querySelector('[data-filter-count]');
      if (generoBadge) {
        generoBadge.textContent = generoCount;
        generoBadge.style.display = generoCount > 0 ? 'inline-flex' : 'none';
      }
    }

    if (tallaPill) {
      var tallaCount = section.querySelectorAll('[data-filter-type="talla"]:checked').length;
      var tallaBadge = tallaPill.querySelector('[data-filter-count]');
      if (tallaBadge) {
        tallaBadge.textContent = tallaCount;
        tallaBadge.style.display = tallaCount > 0 ? 'inline-flex' : 'none';
      }
    }
  }

  function updateURLWithCustomFilters(section) {
    var url = buildFilterUrl(section);

    // Update URL without reload
    window.history.replaceState({}, '', url.toString());

    // Update active filter tags
    updateActiveFilterTags(section);
  }

  function updateActiveFilterTags(section) {
    var container = section.querySelector('[data-active-filters]');
    var tagsContainer = section.querySelector('[data-custom-active-tags]');
    var clearBtn = section.querySelector('[data-clear-custom-filters]');
    
    if (!container || !tagsContainer) return;

    // Clear existing custom tags
    tagsContainer.innerHTML = '';

    var hasCustomFilters = false;

    // Add mejor_uso tags
    section.querySelectorAll('[data-custom-checkbox][data-filter-type="mejor_uso"]:checked').forEach(function (cb) {
      hasCustomFilters = true;
      var tag = createFilterTag('Mejor Uso', cb.value, function () {
        cb.checked = false;
        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
      tagsContainer.appendChild(tag);
    });

    // Add tecnologias tags
    section.querySelectorAll('[data-custom-checkbox][data-filter-type="tecnologias"]:checked').forEach(function (cb) {
      hasCustomFilters = true;
      var tag = createFilterTag('Tecnología', cb.value, function () {
        cb.checked = false;
        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
      tagsContainer.appendChild(tag);
    });

    // Add genero tags
    section.querySelectorAll('[data-custom-checkbox][data-filter-type="genero"]:checked').forEach(function (cb) {
      hasCustomFilters = true;
      var tag = createFilterTag('Genero', cb.value, function () {
        cb.checked = false;
        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
      tagsContainer.appendChild(tag);
    });

    // Add talla tags
    section.querySelectorAll('[data-custom-checkbox][data-filter-type="talla"]:checked').forEach(function (cb) {
      hasCustomFilters = true;
      var tag = createFilterTag('Talla', cb.value, function () {
        cb.checked = false;
        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
      tagsContainer.appendChild(tag);
    });

    // Show/hide container
    container.style.display = hasCustomFilters ? 'flex' : 'none';

    // Setup clear button
    if (clearBtn && !clearBtn._hasListener) {
      clearBtn._hasListener = true;
      clearBtn.addEventListener('click', function () {
        section.querySelectorAll('[data-custom-checkbox]:checked').forEach(function (cb) {
          cb.checked = false;
        });
        applyCustomFilters(section);
        updateCustomFilterCounts(section);
        updateURLWithCustomFilters(section);
      });
    }
  }

  function createFilterTag(label, value, onRemove) {
    var tag = document.createElement('button');
    tag.type = 'button';
    tag.className = 'tnf-mc__active-tag';
    tag.innerHTML = label + ': ' + value + ' <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    tag.addEventListener('click', onRemove);
    return tag;
  }

  /* ==========================================================================
     Filter Pills & Sort Dropdown
     ========================================================================== */

  function initFilterPills(section) {
    var pills = section.querySelectorAll('[data-filter-pill]');
    pills.forEach(function (pill) {
      var btn = pill.querySelector('.tnf-mc__pill-btn');
      var closeBtn = pill.querySelector('[data-close-dropdown]');
      var overlay = pill.querySelector('[data-pill-overlay]');

      if (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          var wasOpen = pill.classList.contains('is-open');
          closeAllDropdowns(section);
          if (!wasOpen) {
            pill.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          pill.classList.remove('is-open');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      if (overlay) {
        overlay.addEventListener('click', function () {
          pill.classList.remove('is-open');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  function initSortDropdown(section) {
    var sortContainer = section.querySelector('[data-sort-dropdown]');
    if (!sortContainer) return;

    var btn = sortContainer.querySelector('.tnf-mc__sort-btn');
    var overlay = sortContainer.querySelector('[data-pill-overlay]');
    var options = sortContainer.querySelectorAll('.tnf-mc__sort-option');

    if (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasOpen = sortContainer.classList.contains('is-open');
        closeAllDropdowns(section);
        if (!wasOpen) {
          sortContainer.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function () {
        sortContainer.classList.remove('is-open');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        var sortValue = option.getAttribute('data-sort-value');
        if (!sortValue) return;

        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortValue);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  function initFilterCheckboxes(section) {
    var checkboxes = section.querySelectorAll('[data-filter-checkbox]');
    checkboxes.forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        if (isMobileViewport()) return;
        applyFilters(section);
      });
    });
  }

  function initPriceFilters(section) {
    var priceFilters = section.querySelectorAll('[data-price-filter]');

    function formatPriceValue(value) {
      var amount = Number(value);

      if (!Number.isFinite(amount)) {
        amount = 0;
      }

      return '$' + new Intl.NumberFormat(document.documentElement.lang || 'es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    }

    priceFilters.forEach(function (priceFilter) {
      var minInput = priceFilter.querySelector('[data-price-min]');
      var maxInput = priceFilter.querySelector('[data-price-max]');
      var minRange = priceFilter.querySelector('[data-price-range-input="min"]');
      var maxRange = priceFilter.querySelector('[data-price-range-input="max"]');
      var progress = priceFilter.querySelector('[data-price-progress]');
      var minBound = priceFilter.querySelector('[data-price-bound="min"]');
      var maxBound = priceFilter.querySelector('[data-price-bound="max"]');

      if (!(minInput instanceof HTMLInputElement) ||
          !(maxInput instanceof HTMLInputElement) ||
          !(minRange instanceof HTMLInputElement) ||
          !(maxRange instanceof HTMLInputElement) ||
          !(progress instanceof HTMLElement)) {
        return;
      }

      var minLimit = parseInt(minRange.min || '0', 10);
      var maxLimit = parseInt(maxRange.max || '0', 10);
      var configuredStep = parseInt(minRange.step || maxRange.step || '1', 10);
      var step = Math.max(Math.min(configuredStep, Math.max(maxLimit - minLimit, 1)), 1);

      function getSafeValue(input, fallbackValue) {
        var parsed = parseInt(input.value || String(fallbackValue), 10);
        return Number.isNaN(parsed) ? fallbackValue : parsed;
      }

      function updateProgress(minValue, maxValue) {
        var denominator = Math.max(maxLimit - minLimit, 1);
        var minPercent = ((minValue - minLimit) / denominator) * 100;
        var maxPercent = ((maxValue - minLimit) / denominator) * 100;

        progress.style.left = minPercent + '%';
        progress.style.width = Math.max(maxPercent - minPercent, 0) + '%';

        if (minBound instanceof HTMLElement) {
          minBound.textContent = formatPriceValue(minValue);
        }

        if (maxBound instanceof HTMLElement) {
          maxBound.textContent = formatPriceValue(maxValue);
        }
      }

      function syncPriceRange(source) {
        var minValue = getSafeValue(minRange, minLimit);
        var maxValue = getSafeValue(maxRange, maxLimit);

        minValue = Math.min(Math.max(minValue, minLimit), maxLimit);
        maxValue = Math.min(Math.max(maxValue, minLimit), maxLimit);

        if (source === 'min' && minValue > maxValue - step) {
          minValue = Math.max(minLimit, maxValue - step);
        }

        if (source === 'max' && maxValue < minValue + step) {
          maxValue = Math.min(maxLimit, minValue + step);
        }

        if (minValue > maxValue) {
          minValue = Math.min(minValue, maxValue);
        }

        minRange.value = String(minValue);
        maxRange.value = String(maxValue);
        minInput.value = minValue > minLimit ? String(minValue) : '';
        maxInput.value = maxValue < maxLimit ? String(maxValue) : '';

        updateProgress(minValue, maxValue);
      }

      minRange.addEventListener('input', function () {
        syncPriceRange('min');
      });

      maxRange.addEventListener('input', function () {
        syncPriceRange('max');
      });

      [minRange, maxRange].forEach(function (rangeInput) {
        rangeInput.addEventListener('change', function () {
          if (isMobileViewport()) return;
          applyFilters(section);
        });
      });

      syncPriceRange();
    });

    var applyBtns = section.querySelectorAll('[data-apply-price]');
    applyBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (isMobileViewport()) return;
        applyFilters(section);
      });
    });

    // Apply on Enter key in price inputs
    var priceInputs = section.querySelectorAll('.tnf-mc__price-input');
    priceInputs.forEach(function (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (isMobileViewport()) return;
          applyFilters(section);
        }
      });
    });
  }

  function initMobileFilterDrawer(section) {
    var trigger = section.querySelector('[data-mobile-filter-trigger]');
    var drawer = section.querySelector('[data-mobile-filter-drawer]');
    var applyButton = section.querySelector('[data-mobile-filter-apply]');
    var closeButtons = section.querySelectorAll('[data-mobile-filter-close]');

    if (!trigger || !drawer) return;

    var lockedScrollY = 0;

    function lockBodyScroll() {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + lockedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    }

    function unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, lockedScrollY);
    }

    function openDrawer() {
      if (!isMobileViewport()) return;

      closeAllDropdowns(section);
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('tnf-mc-mobile-filters-open');
      lockBodyScroll();
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('tnf-mc-mobile-filters-open');
      unlockBodyScroll();
      closeAllDropdowns(section);
    }

    function applyMobileFilters() {
      var url = buildFilterUrl(section);
      var hasNativeSelections = hasSelectedNativeFilters(section) || hasFilledPriceFilters(section);

      if (hasNativeSelections) {
        window.location.href = url.toString();
        return;
      }

      window.history.replaceState({}, '', url.toString());
      applyCustomFilters(section);
      updateCustomFilterCounts(section);
      updateActiveFilterTags(section);
      closeDrawer();
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openDrawer();
    });

    closeButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeDrawer();
      });
    });

    if (applyButton) {
      applyButton.addEventListener('click', function (event) {
        event.preventDefault();
        applyMobileFilters();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    window.addEventListener('resize', function () {
      if (!isMobileViewport() && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  }

  /**
   * Collects all active filters from checkboxes and price inputs,
   * builds a URL with query params, and navigates to it.
   */
  function applyFilters(section) {
    var url = buildFilterUrl(section);
    window.location.href = url.toString();
  }

  function closeAllDropdowns(section) {
    var pills = section.querySelectorAll('.tnf-mc__pill.is-open');
    pills.forEach(function (pill) {
      pill.classList.remove('is-open');
      var btn = pill.querySelector('.tnf-mc__pill-btn');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });

    var sortContainer = section.querySelector('.tnf-mc__sort.is-open');
    if (sortContainer) {
      sortContainer.classList.remove('is-open');
      var sortBtn = sortContainer.querySelector('.tnf-mc__sort-btn');
      if (sortBtn) sortBtn.setAttribute('aria-expanded', 'false');
    }
  }

  function initCloseOnOutsideClick(section) {
    document.addEventListener('click', function (e) {
      var activeCard = getActiveInteractionCard(section);
      if (activeCard && !activeCard.contains(e.target) && !e.target.closest('[data-popup-overlay]')) {
        clearActiveInteractionCard(section, activeCard);
      }

      if (e.target.closest('.tnf-mc__mobile-filter-panel')) return;
      if (!e.target.closest('.tnf-mc__pill') && !e.target.closest('.tnf-mc__sort')) {
        closeAllDropdowns(section);
      }
    });
  }

  /* ==========================================================================
     Card Image Slider
     ========================================================================== */

  function initCardSlider(card) {
    var slider = card.querySelector('[data-card-slider]');
    if (!slider) return;

    var currentIndex = 0;

    function getSliderParts() {
      return {
        imagesTrack: slider.querySelector('.tnf-pccarousel__images-track'),
        slides: slider.querySelectorAll('.tnf-pccarousel__image-slide'),
        dots: slider.querySelectorAll('.tnf-pccarousel__img-dot')
      };
    }

    function goToSlide(index) {
      var sliderParts = getSliderParts();
      var total = sliderParts.slides.length;

      if (!sliderParts.imagesTrack || total === 0) return;

      if (index < 0) index = total - 1;
      if (index >= total) index = 0;
      currentIndex = index;

      sliderParts.imagesTrack.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';

      sliderParts.slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === currentIndex);
      });

      sliderParts.dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === currentIndex);
      });
    }

    card.__tnfCardSlider = {
      reset: function () {
        currentIndex = 0;
        goToSlide(0);
      }
    };

    if (slider.__tnfSliderBound) {
      card.__tnfCardSlider.reset();
      return;
    }

    slider.addEventListener('click', function (e) {
      var prevBtn = e.target.closest('[data-card-prev]');
      if (prevBtn && slider.contains(prevBtn)) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(currentIndex - 1);
        return;
      }

      var nextBtn = e.target.closest('[data-card-next]');
      if (nextBtn && slider.contains(nextBtn)) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(currentIndex + 1);
        return;
      }

      var dot = e.target.closest('[data-card-dot]');
      if (dot && slider.contains(dot)) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(parseInt(dot.getAttribute('data-card-dot') || '0', 10));
      }
    });

    slider.__tnfSliderBound = true;
    card.__tnfCardSlider.reset();
  }

  /* ==========================================================================
     Color Swatches
     ========================================================================== */

  function initSwatches(card) {
    var section = card.closest('.tnf-mc');
    var swatches = card.querySelectorAll('[data-swatch-btn]');
    if (swatches.length <= 1) return;

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        e.preventDefault();

        if (section) {
          setActiveInteractionCard(section, card);
        }

        swatches.forEach(function (s) {
          s.classList.remove('is-active');
        });
        swatch.classList.add('is-active');

        var variantId = swatch.getAttribute('data-variant-id');
        if (variantId) {
          refreshCardDiscount(card, variantId);
        }

        syncSwatchOverflowContainers(card);
      });
    });
  }

  /* ==========================================================================
     Color Group Swatches (Product Grouping - inline swap)
     ========================================================================== */

  function initGroupSwatches(card) {
    var section = card.closest('.tnf-mc');
    var swatches = card.querySelectorAll('[data-group-swatch]');
    if (swatches.length <= 1) return;

    swatches.forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (section) {
          setActiveInteractionCard(section, card);
        }

        swatches.forEach(function (s) {
          s.classList.remove('is-active');
          s.removeAttribute('aria-current');
        });
        swatch.classList.add('is-active');
        swatch.setAttribute('aria-current', 'true');

        var newUrl = swatch.getAttribute('data-product-url');
        var newTitle = swatch.getAttribute('data-product-title');
        var newImages = parseGroupedImagePayload(swatch);
        var newVariantId = swatch.getAttribute('data-product-variant-id');
        var newSizePayload = parseGroupedSizePayload(swatch);
        syncSelectItemCardTracking(card, swatch);

        var nameEl = card.querySelector('.tnf-pccarousel__product-name');
        if (nameEl) {
          nameEl.textContent = newTitle;
        }
        if (newUrl) {
          card.setAttribute('data-product-url', newUrl);
        }

        refreshCardDiscount(card, newVariantId);
        if (card.__tnfSizePicker) {
          card.__tnfSizePicker.sync(newSizePayload);
        }

        syncCardSliderMedia(card, newUrl, newTitle, newImages);
        syncSwatchOverflowContainers(card);
      });
    });
  }

  /* ==========================================================================
     Size Picker + Add to Cart
     ========================================================================== */

  function initSizePicker(card) {
    var section = card.closest('.tnf-mc');
    var elegirBtn = card.querySelector('[data-elegir-talla]');
    var picker = card.querySelector('[data-size-picker]');
    var addBtn = card.querySelector('[data-add-to-cart]');
    if (!picker) return;

    var selectedVariantId = null;
    var isExpanded = false;

    function getPills() {
      return picker.querySelectorAll('[data-size-pill]');
    }

    function renderSizePills(payload) {
      var pillsContainer = picker.querySelector('.tnf-pccarousel__size-pills');
      var label = payload && payload.label ? payload.label : 'Talla';
      var options = payload && Array.isArray(payload.options) ? payload.options : [];
      var labelEl = picker.querySelector('.tnf-pccarousel__size-label');

      if (labelEl) {
        labelEl.textContent = label;
      }

      if (!pillsContainer) return;

      pillsContainer.innerHTML = options.map(function (option) {
        var value = option && option.value != null ? String(option.value) : '';
        var variantId = option && option.variantId != null ? String(option.variantId) : '';
        var isAvailable = Boolean(option && option.available && variantId);
        return '<button type="button" class="tnf-pccarousel__size-pill' +
          (isAvailable ? '' : ' is-unavailable') +
          '" data-size-pill data-variant-id="' + escapeHtml(variantId) +
          '" data-size-value="' + escapeHtml(value) + '"' +
          (isAvailable ? '' : ' disabled') +
          ' aria-label="' + escapeHtml(value) +
          '" title="' + escapeHtml(value) + '"><span>' +
          escapeHtml(value) + '</span></button>';
      }).join('');
    }

    function resetSizePicker() {
      if (elegirBtn) {
        elegirBtn.classList.remove('is-active', 'is-ready');
        elegirBtn.textContent = 'Elegir Talla';
      }

      picker.classList.remove('is-open');

      if (addBtn) addBtn.style.display = 'none';

      getPills().forEach(function (pill) {
        pill.classList.remove('is-selected');
      });

      selectedVariantId = null;
      isExpanded = false;

      if (section) {
        scheduleDeferredClientSyncFlush(section);
      }
    }

    function syncSizePicker(payload) {
      var shouldStayOpen = isExpanded;
      renderSizePills(payload);
      resetSizePicker();

      if (!shouldStayOpen || !getPills().length) return;

      picker.classList.add('is-open');
      isExpanded = true;

      if (elegirBtn) {
        elegirBtn.classList.add('is-active');
        elegirBtn.classList.remove('is-ready');
        elegirBtn.textContent = 'Elegir Talla';
      }
    }

    card.__tnfSizePicker = {
      reset: resetSizePicker,
      sync: syncSizePicker
    };

    document.addEventListener(sizePickerOpenEventName, function (event) {
      var sourceCard = event.detail && event.detail.card;
      if (!sourceCard || sourceCard === card || !isExpanded) return;
      resetSizePicker();
    });

    if (elegirBtn) {
      elegirBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (window.innerWidth <= 749) {
          openMobilePopup(card);
          return;
        }

        if (!getPills().length) return;

        if (!isExpanded) {
          document.dispatchEvent(new CustomEvent(sizePickerOpenEventName, {
            detail: { card: card }
          }));
          elegirBtn.classList.add('is-active');
          picker.classList.add('is-open');
          isExpanded = true;
        } else if (selectedVariantId) {
          addToCart(selectedVariantId, elegirBtn, card, function () {
            resetSizePicker();
          });
        }
      });
    }

    picker.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-size-pill]');
      if (!pill || !picker.contains(pill)) return;

      e.preventDefault();
      e.stopPropagation();

      if (pill.disabled) return;

      var variantId = pill.getAttribute('data-variant-id');
      if (!variantId) return;

      getPills().forEach(function (currentPill) {
        currentPill.classList.remove('is-selected');
      });

      pill.classList.add('is-selected');
      selectedVariantId = variantId;
      refreshCardDiscount(card, variantId);

      if (elegirBtn) {
        elegirBtn.classList.add('is-ready');
        elegirBtn.textContent = 'Agregar al carrito';
      }

      if (addBtn) {
        addBtn.style.display = 'none';
      }
    });

    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addToCart(selectedVariantId, addBtn, card);
      });
    }
  }

  function addToCart(variantId, btn, card, onSuccess) {
    if (!variantId || btn.getAttribute('aria-busy') === 'true') return;

    btn.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'tnf-main-collection',
            data: {
              source: 'product-form-component',
              itemCount: 1
            }
          }
        }));
        if (onSuccess) onSuccess();
      })
      .catch(function () {
        var productUrl = card.getAttribute('data-product-url');
        if (productUrl) {
          window.location.href = productUrl;
        }
      })
      .finally(function () {
        btn.removeAttribute('aria-busy');
      });
  }

  function openMobilePopup(card) {
    var section = card.closest('.tnf-mc');
    if (!section) return;

    var overlay = section.querySelector('[data-popup-overlay]');
    var sliderTarget = section.querySelector('[data-popup-slider]');
    var infoTarget = section.querySelector('[data-popup-info]');
    var quickbuyTarget = section.querySelector('[data-popup-quickbuy]');
    var closeBtn = section.querySelector('[data-popup-close]');
    if (!overlay || !sliderTarget || !infoTarget || !quickbuyTarget) return;

    var sliderSource = card.querySelector('[data-card-slider]');
    var infoSource = card.querySelector('.tnf-pccarousel__info');
    var quickbuySource = card.querySelector('.tnf-pccarousel__quick-buy');

    sliderTarget.innerHTML = '';
    infoTarget.innerHTML = '';
    quickbuyTarget.innerHTML = '';

    if (sliderSource) sliderTarget.appendChild(sliderSource.cloneNode(true));
    if (infoSource) infoTarget.appendChild(infoSource.cloneNode(true));

    if (quickbuySource) {
      var qbClone = quickbuySource.cloneNode(true);
      var popupElegirBtn = qbClone.querySelector('[data-elegir-talla]');
      if (popupElegirBtn) popupElegirBtn.style.display = 'none';

      var popupPicker = qbClone.querySelector('[data-size-picker]');
      if (popupPicker) popupPicker.classList.add('is-open');

      var popupAddBtn = qbClone.querySelector('[data-add-to-cart]');
      if (popupAddBtn) {
        popupAddBtn.style.display = '';
        popupAddBtn.disabled = true;
        popupAddBtn.setAttribute('aria-disabled', 'true');
      }

      quickbuyTarget.appendChild(qbClone);
    }

    initPopupSlider(sliderTarget);
    initPopupSwatches(quickbuyTarget, card);
    initPopupSizePicker(quickbuyTarget, card, overlay);

    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    function closePopup() {
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      sliderTarget.innerHTML = '';
      infoTarget.innerHTML = '';
      quickbuyTarget.innerHTML = '';
      scheduleDeferredClientSyncFlush(section);
    }

    if (closeBtn) {
      closeBtn.onclick = function (e) {
        e.preventDefault();
        closePopup();
      };
    }

    overlay.onclick = function (e) {
      if (e.target === overlay) {
        closePopup();
      }
    };
  }

  function initPopupSwatches(container, originalCard) {
    syncSwatchOverflowContainers(container);
    bindPopupSwatchSet(container, originalCard, '[data-group-swatch]', 'data-product-url');
    bindPopupSwatchSet(container, originalCard, '[data-swatch-btn]', 'data-variant-id');
  }

  function bindPopupSwatchSet(container, originalCard, selector, identityAttribute) {
    var popupSwatches = container.querySelectorAll(selector);
    if (!popupSwatches.length) return;

    popupSwatches.forEach(function (popupSwatch, popupIndex) {
      popupSwatch.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var originalSwatch = findMatchingOriginalSwatch(
          originalCard,
          selector,
          identityAttribute,
          popupSwatch,
          popupIndex
        );

        if (!originalSwatch) return;

        originalSwatch.click();
        openMobilePopup(originalCard);
      });
    });
  }

  function findMatchingOriginalSwatch(originalCard, selector, identityAttribute, popupSwatch, popupIndex) {
    var originalSwatches = originalCard.querySelectorAll(selector);
    if (!originalSwatches.length) return null;

    var identityValue = popupSwatch.getAttribute(identityAttribute);
    if (identityValue) {
      for (var index = 0; index < originalSwatches.length; index += 1) {
        if (originalSwatches[index].getAttribute(identityAttribute) === identityValue) {
          return originalSwatches[index];
        }
      }
    }

    return originalSwatches[popupIndex] || null;
  }

  function initPopupSlider(container) {
    var imagesTrack = container.querySelector('.tnf-pccarousel__images-track');
    var slides = container.querySelectorAll('.tnf-pccarousel__image-slide');
    var dots = container.querySelectorAll('.tnf-pccarousel__img-dot');
    var prevBtn = container.querySelector('[data-card-prev]');
    var nextBtn = container.querySelector('[data-card-next]');

    if (!imagesTrack || slides.length <= 1) return;

    var currentIndex = 0;
    var total = slides.length;

    function goToSlide(index) {
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;
      currentIndex = index;
      imagesTrack.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';
      slides.forEach(function (slide, slideIndex) {
        slide.classList.toggle('is-active', slideIndex === currentIndex);
      });
      dots.forEach(function (dot, dotIndex) {
        dot.classList.toggle('is-active', dotIndex === currentIndex);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(currentIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(currentIndex + 1);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goToSlide(parseInt(dot.getAttribute('data-card-dot') || '0', 10));
      });
    });
  }

  function initPopupSizePicker(container, originalCard, overlay) {
    var section = originalCard.closest('.tnf-mc');
    var pills = container.querySelectorAll('[data-size-pill]');
    var addBtn = container.querySelector('[data-add-to-cart]');
    var selectedVariantId = null;

    if (addBtn) {
      addBtn.style.display = '';
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (pill.disabled) return;

        var variantId = pill.getAttribute('data-variant-id');
        if (!variantId) return;

        pills.forEach(function (currentPill) {
          currentPill.classList.remove('is-selected');
        });
        pill.classList.add('is-selected');
        selectedVariantId = variantId;

        if (addBtn) {
          addBtn.disabled = false;
          addBtn.setAttribute('aria-disabled', 'false');
        }
      });
    });

    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!selectedVariantId || addBtn.disabled) return;
        addToCart(selectedVariantId, addBtn, originalCard, function () {
          overlay.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          if (section) {
            scheduleDeferredClientSyncFlush(section);
          }
        });
      });
    }
  }

})();
